import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  archiveUser,
  unArchiveUser,
  getUser,
} from '../../redux/actions/userActions';
import {
  cancelText,
  editText,
  archiveText,
  unArchiveText,
  archiveChallengeText,
  unArchiveChallengeText,
  devicesText,
  staffText,
  checkinText,
  infoText,
 
} from '../shared/SharedInternationalizations';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonTextSizes,
} from '../shared/Button';
import SvgUpArrowIcon from '../svg/UpArrowIcon';
import Overlay from '../shared/Overlay';
import UserCheckinPreferences from './userDisplay/UserCheckinPreferences';
import UserStaffProfile from './userDisplay/UserStaffProfile';
import UserInfo from './userDisplay/UserInfo';
import UserDevices from './userDisplay/UserDevices';
import { loadAlarmDeviceTypes } from '../../redux/actions/alarmDeviceActions';
import { loadAlertDeviceTypes } from '../../redux/actions/alertDeviceActions';

import AccessControl from '../../AccessControl';
import permissions from '../../permissionsList';
import { loadUnit } from '../../redux/actions/unitActions';



/**
 * The display component for the full view of a single user
 * @param {number} id - The id of the user to be displayed (passed by React Router route parameter)
 * @param {string} mode - Determines the view mode for the component. If blank, user will be displayed normally, if "edit" user edit form will be displayed (passed by React Router route parameter)
 * @description - The component uses connected data from Redux to cross-reference the user ID to the full user data to be displayed.
 * @returns {object} - The data from the user edit form
 */

export function UnconnectedUserDisplay(props) {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const {
    checkinModes,
    user,
    userStatuses,
    unitsObj,
    alertGroupPermissions,
    alertDeviceAccess,
    massNotificationAccess,
  } = props;
  const { search, state, pathname } = location;

  const searchParams = new URLSearchParams(search);
  const view = searchParams.get('view');
  const [showArchive, setShowArchive] = useState(false);
  const [showUnArchive, setShowUnArchive] = useState(false);

  let urlParams = '';
  // This checks the state being passed by the history object for the url of the page that navigated to this page. This is used to ensure that the "return to users" link is correct
  if (state && state.previous) {
    const address = state.previous;
    const addressArr = address.split('?');
    urlParams = addressArr[1];
  }
  // Get the current full url. props.location.search contains any query parameters that might be present.
  const currentLocation = pathname + search;

  // If the page is loaded but there's no user data, this will provide a blank screen while the interface is loading.
  // This is to prevent errors when a user navigates to this page when the data hasn't fully been loaded by the api.
  const blankUser = {
    id: '',
    username: '',
    first_name: '',
    last_name: '',
    middle_name: '',
    email: '',
    suffix: '',
    title: '',
    preferred_name: '',
    phone: '',
    date_of_birth: '',
    groups: [],
    user_permissions: [],
    units: [],
    active_unit: '',
    is_active: true,
    gender: '',
    notes: '',
    preferences: null,
    photo: {},
    status: '',
    checkin_profile: null,
    alarm_devices: [],
    alert_devices: [],
    Primary_Phone_Number: '',
    Work_Phone_Number: '',
    Text_Message: '',
    Secondary_Phone_Number: '',
    Work_Email_Address: '',
    Personal_Email_Address: '',
    Secondary_Email_Address: '',
  };
  let userObj = blankUser;
  if (user) {
    userObj = user;
  }

  // Load alarm device types, alert device types, and the full user data for the specific user being views once when the component loads
  useEffect(() => {
    props.dispatch(loadAlarmDeviceTypes());
    props.dispatch(loadAlertDeviceTypes());
    props.dispatch(getUser(params.id));
    if (userObj.active_unit) {
      props.dispatch(loadUnit(userObj.active_unit));
    }
  }, []); // eslint-disable-line

  const archiveButtonConfirmationText = (
    <FormattedMessage
      id="userDisplay.archiveConfirmationText"
      defaultMessage="Yes I want to Archive this user"
      description="Button to archive the user"
    />
  );

  const unArchiveButtonConfirmationText = (
    <FormattedMessage
      id="userDisplay.unArchiveConfirmationText"
      defaultMessage="Yes I want to Unarchive this user"
      description="Button to unarchive the user"
    />
  );

  const backText = (
    <FormattedMessage
      id="userDisplay.backText"
      defaultMessage="Back to Users"
      description="Link back to the users interface"
    />
  );

  const archiveDialogText = (
    <FormattedMessage
      id="userDisplay.archiveDialogText"
      defaultMessage="Please be aware: {br}Archiving a user is similar to deleting it,
      doing so will remove it from view and it will no longer be
      accessible. This will also remove all device and unit associations to this "
      values={{
        br: <br />,
      }}
      description="Text that displays when archiving a user to warn the user what will happen when the user is archived"
    />
  );
  const unArchiveDialogText = (
    <FormattedMessage
      id="userDisplay.unArchiveDialogText"
      defaultMessage="Please be aware: {br}Unarchiving a user is making a user active,
      doing so will add it to the view and it will be accessible."
      values={{
        br: <br />,
      }}
      description="Text that displays when unarchiving a user to warn the user what will happen when the user is unarchived"
    />
  );

  function handleArchive() {
    props.dispatch(archiveUser(userObj, navigate));
  }

  function handleUnArchive() {
    props.dispatch(unArchiveUser(userObj, navigate));
  }

  function archiveConfirmation() {
    setShowArchive(true);
  }

  function cancelArchive() {
    setShowArchive(false);
  }

  function unArchiveConfirmation() {
    setShowUnArchive(true);
  }

  function cancelUnArchive() {
    setShowUnArchive(false);
  }

  let unitCheckin = false;
  if (
    userObj.active_unit &&
    unitsObj &&
    unitsObj[userObj.active_unit] &&
    unitsObj[userObj.active_unit].checkin_profile &&
    unitsObj[userObj.active_unit].checkin_profile.is_active
  ) {
    unitCheckin = true;
  }

  /**
   * Function to display the archive challenge notification modal
   */
  function displayArchive() {
    if (showArchive) {
      return (
        <Overlay
          rootUrl={`/users/${userObj.id}/view/`}
          canClose={false}
          data-test="user-archive-challenge-overlay"
          useFunction={true}
          onClick={cancelArchive}>
          <div className="content-container content-container--column">
            <h1 className="no-margin text-center">
              {archiveChallengeText} {userObj.first_name} {userObj.last_name}?
            </h1>
            {archiveDialogText}
            <hr />
            <div className="flex-end">
              <Button
                onClick={cancelArchive}
                text={cancelText}
                buttonType={buttonTypes.noButton}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin margin-right-15 js-cancel-archive-button"
              />
              <Button
                onClick={handleArchive}
                text={archiveButtonConfirmationText}
                buttonType={buttonTypes.danger}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin js-submit-archive-button"
              />
            </div>
          </div>
        </Overlay>
      );
    } else {
      return '';
    }
  }

  /**
   * Function to display the unarchive challenge notification modal
   */
  function displayUnArchive() {
    if (showUnArchive) {
      return (
        <Overlay
          rootUrl={`/users/${userObj.id}/view/`}
          canClose={false}
          data-test="user-unarchive-challenge-overlay"
          useFunction={true}
          onClick={cancelArchive}>
          <div className="content-container content-container--column">
            <h1 className="no-margin">
              {unArchiveChallengeText} {userObj.first_name} {userObj.last_name}?
            </h1>
            {unArchiveDialogText}
            <hr />
            <div className="flex-end">
              <Button
                onClick={cancelUnArchive}
                text={cancelText}
                buttonType={buttonTypes.noButton}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin margin-right-15"
              />
              <Button
                onClick={handleUnArchive}
                text={unArchiveButtonConfirmationText}
                buttonType={buttonTypes.success}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin"
              />
            </div>
          </div>
        </Overlay>
      );
    } else {
      return '';
    }
  }

  // Displays the correct interface based on what query parameters are passed in the URL
  function displayInfo() {
    switch (view) {
      case 'devices':
        return (
          <div data-test="devices-component">
            <UserDevices
              id={userObj.id}
              currentLocation={currentLocation}
              alarmDeviceList={userObj.alarm_devices}
              alertDeviceList={userObj.alert_devices}
              userObj={userObj}
            />
          </div>
        );
      case 'checkin':
        return (
          <div data-test="checkin-component">
            <UserCheckinPreferences
              checkin_profile={userObj.checkin_profile}
              userStatuses={userStatuses}
              currentLocation={currentLocation}
              checkinModes={checkinModes}
              userObj={userObj}
              unitsObj={unitsObj}
              unitCheckin={unitCheckin}
            />
          </div>
        );

      case 'staff':
        return (
          <div data-test="staff-profile-component">
            <UserStaffProfile
              staffPreferences={userObj.preferences}
              currentLocation={currentLocation}
              userObj={userObj}
              alertGroupPermissions={alertGroupPermissions}
              alertDeviceAccess={alertDeviceAccess}
            />
          </div>
        );

      default:
        return (
          <div data-test="info-component">
            <UserInfo
              user={userObj}
              currentLocation={currentLocation}
            />
          </div>
        );
    }
  }

  // constructs the display name for the user by checking for parts of the name, like title, and displaying it if it's there.
  // It's done as a ternary operator because if it was just put there without a check it would display extra spaces or "undefined"
  const userDisplayName = `${userObj.title ? userObj.title : ''} ${
    userObj.first_name
  } ${userObj.preferred_name ? `"${userObj.preferred_name}"` : ''} ${
    userObj.middle_name ? userObj.middle_name : ''
  } ${userObj.last_name} ${userObj.suffix ? userObj.suffix : ''}`;

  return (
    <div className="container" data-test="user-display">
      <FormattedMessage
        id="userDisplay.browserTabInfo"
        defaultMessage="Users | {username}"
        description="Done button"
        values={{
          username: `${userObj.title ? userObj.title : ''} ${
            userObj.first_name ? userObj.first_name : ''
          } ${userObj.last_name ? userObj.last_name : ''} ${
            userObj.suffix ? userObj.suffix : ''
          }`,
        }}>
        {(txt) => (
          <Helmet>
            <title>{txt}</title>
          </Helmet>
        )}
      </FormattedMessage>
      <div className="layout-container content-container content-container--column content-container--shadow">
        <div className="width-100 align-center flex-around margin-bottom-10">
          <Link
            className="link-text align-center"
            to={urlParams !== '' ? `/users/?${urlParams}` : '/users/'}
            data-test="user-display-cancel-link">
            <SvgUpArrowIcon className="rotate-90-ccw margin-right-10" />
            {backText}
          </Link>
        </div>
        <div
          className="content-display__container content-display__container--column align-center"
          data-test="user-name-title">
          <FormattedMessage
            id="userDisplay.usernameDisplayText"
            data-test="header-text-wrapper"
            defaultMessage="View Details for User: {user}"
            description="The title for the page to edit a unit"
            values={{
              user: userDisplayName,
            }}>
            {(txt) => (
              <h1
                className="no-margin text-deep-blue text-center"
                data-test="header-text">
                {txt}
              </h1>
            )}
          </FormattedMessage>
        </div>
        <div className="tab-bar" data-test="tab-bar">
          <Link
            to=""
            className={
              view === null
                ? 'text-medium tab-bar__tab tab-bar__tab--active'
                : 'text-medium  tab-bar__tab'
            }
            data-test="info-tab">
            {infoText}
          </Link>

          {userObj.checkin_profile || unitCheckin ? (
            <Link
              to="?view=checkin"
              className={
                view === 'checkin'
                  ? 'text-medium tab-bar__tab tab-bar__tab--active'
                  : 'text-medium  tab-bar__tab'
              }
              data-test="checkin-tab">
              {checkinText}
            </Link>
          ) : (
            ''
          )}
          {userObj.preferences ? (
            <Link
              to="?view=staff"
              className={
                view === 'staff'
                  ? 'text-medium tab-bar__tab tab-bar__tab--active'
                  : 'text-medium  tab-bar__tab'
              }
              data-test="staff-tab">
              {staffText}
            </Link>
          ) : (
            ''
          )}
          {userObj.alarm_devices || userObj.alert_devices ? (
            <Link
              to="?view=devices"
              className={
                view === 'devices'
                  ? 'text-medium tab-bar__tab tab-bar__tab--active'
                  : 'text-medium  tab-bar__tab'
              }
              data-test="devices-tab">
              {devicesText}
            </Link>
          ) : (
            ''
          )}
        </div>
        {displayInfo()}
        <div className="flex-spread width-100">
          <AccessControl
            data-test="access-control"
            feature={permissions.static.users.editUser}
            ifNotAllowed={<div></div>}
            permissionMessage={true}>
            {userObj.is_active ? (
              props.currentUser.id !== userObj.id && !userObj.is_staff && !userObj.is_system_user ? (
                <Button
                  onClick={archiveConfirmation}
                  text={archiveText}
                  buttonType={buttonTypes.danger}
                  buttonSize={buttonSizes.small}
                  buttonTextSize={buttonTextSizes.mediumSmall}
                  additionalStyles="no-margin js-archive-button"
                  data-test="user-full-view-archive-button"
                />
              ) : (
                <div></div>
              )
            ) : (
              <Button
                onClick={unArchiveConfirmation}
                text={unArchiveText}
                buttonType={buttonTypes.success}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin  js-unarchive-button"
                data-test="user-full-view-unarchive-button"
              />
            )}
          </AccessControl>

          <AccessControl
            data-test="access-control"
            feature={permissions.static.users.editUser}
            userToEdit={userObj.id}>
            {userObj.is_active ? (
              <div className="flex-end align-center">
                <Link
                  className="link-button button-secondary button-sm text-medium-small margin-left-15"
                  data-test="user-edit-button"
                  to={`/users/${userObj.id}/edit`}
                  state={{
                    modal: true,
                    user: userObj.id,
                    previous: currentLocation,
                  }}>
                  {editText}
                </Link>
              </div>
            ) : (
              ''
            )}
          </AccessControl>
        </div>
        <div className="flex-around width-100 margin-top-10">
          <Link
            className="link-text align-center"
            to={urlParams !== '' ? `/users/?${urlParams}` : '/users/'}
            data-test="user-display-cancel-link">
            <SvgUpArrowIcon className="rotate-90-ccw margin-right-10" />
            {backText}
          </Link>
        </div>
        {userObj.is_active ? displayArchive() : displayUnArchive()}
      </div>
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  const id = ownProps.id;
  const user = state.users.users[id];
  const checkinModes = state.users.checkinModes;
  return {
    user,
    unitsObj: state.units.units,
    alarmDeviceTypes: state.devices.alarmDeviceTypes,
    alertDeviceTypes: state.devices.alertDeviceTypes,
    userStatuses: state.users.userStatuses,
    checkinModes,
    currentUser: state.currentUser,
    alertGroupPermissions: state.users.alertGroupPermissions,
    alertDeviceAccess: state.system.sysParams.alertDeviceAccess,
    massNotificationAccess: state.system.sysParams.massNotificationAccess,

  };
};

// Wrapper component to inject route params
function UserDisplayWithRouter() {
  const params = useParams();
  return <ConnectedUserDisplay id={params.id} />;
}

const ConnectedUserDisplay = connect(mapStateToProps)(UnconnectedUserDisplay);

export default UserDisplayWithRouter;
